import { useEffect, useMemo, useState } from "react";
import { Activity } from "lucide-react";

const W = 520, H = 260, PL = 44, PR = 14, PT = 18, PB = 30;
const RPM = Array.from({ length: 41 }, (_, i) => 1500 + i * 125); // 1500 → 6500

// forme de courbe moteur turbo normalisée (0..1) selon le régime
function shape(rpm, peakRpm, width) {
  const x = (rpm - peakRpm) / width;
  const rise = 1 / (1 + Math.exp(-(rpm - 2400) / 350));
  return rise * Math.exp(-(x * x) * (x > 0 ? 0.9 : 0.35));
}

function curve(max, peakRpm, width, floor = 0.18) {
  return RPM.map((r) => max * (floor + (1 - floor) * shape(r, peakRpm, width)));
}

function path(values, yMax) {
  const sx = (i) => PL + (i / (RPM.length - 1)) * (W - PL - PR);
  const sy = (v) => PT + (1 - v / yMax) * (H - PT - PB);
  return values.map((v, i) => `${i ? "L" : "M"}${sx(i).toFixed(1)},${sy(v).toFixed(1)}`).join(" ");
}

export default function DynoChart({ origin, stage1, stage2, originNm, stage1Nm }) {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => { setDrawn(false); const t = setTimeout(() => setDrawn(true), 80); return () => clearTimeout(t); }, [origin, stage1, stage2]);

  const series = useMemo(() => {
    const s = [{ key: "origin", label: "Origine", hp: origin, color: "#9a9a9a", v: curve(origin, 5600, 1500) }];
    if (stage1) s.push({ key: "s1", label: "Stage 1", hp: stage1, color: "#FFED00", v: curve(stage1, 5400, 1550) });
    if (stage2) s.push({ key: "s2", label: "Stage 2", hp: stage2, color: "#E5AB24", v: curve(stage2, 5300, 1600) });
    return s;
  }, [origin, stage1, stage2]);

  const yMax = Math.ceil((Math.max(origin, stage1 || 0, stage2 || 0) * 1.12) / 50) * 50;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f));
  const nmGain = originNm && stage1Nm ? stage1Nm - originNm : null;

  return (
    <div className="hp-card p-5 h-full flex flex-col" data-testid="dyno-chart">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-hp-yellow"><Activity size={16} /><p className="text-xs font-bold uppercase tracking-[0.2em]">Courbe de puissance</p></div>
        <div className="flex gap-3">
          {series.map((s) => <span key={s.key} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-400"><i className="h-0.5 w-4 inline-block" style={{ background: s.color }} />{s.label}</span>)}
        </div>
      </div>
      <div className="relative flex-1">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Courbe de puissance simulée">
        {ticks.map((t) => {
          const y = PT + (1 - t / yMax) * (H - PT - PB);
          return <g key={t}><line x1={PL} x2={W - PR} y1={y} y2={y} stroke="rgba(255,255,255,0.07)" /><text x={PL - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#777">{t}</text></g>;
        })}
        {[2000, 3000, 4000, 5000, 6000].map((r) => {
          const x = PL + ((r - RPM[0]) / (RPM[RPM.length - 1] - RPM[0])) * (W - PL - PR);
          return <text key={r} x={x} y={H - 10} textAnchor="middle" fontSize="9" fill="#777">{r}</text>;
        })}
        <text x={W - PR} y={H - 10} textAnchor="end" fontSize="8" fill="#555">tr/min</text>
        <text x={PL - 6} y={10} textAnchor="end" fontSize="8" fill="#555">ch</text>
        {series.map((s, i) => (
          <g key={s.key}>
            <path d={path(s.v, yMax)} fill="none" stroke={s.color} strokeWidth={s.key === "origin" ? 1.5 : 2.5} strokeLinecap="round"
              style={{ strokeDasharray: 1400, strokeDashoffset: drawn ? 0 : 1400, transition: `stroke-dashoffset 1.8s cubic-bezier(.4,0,.2,1) ${i * 0.35}s`, filter: s.key !== "origin" ? `drop-shadow(0 0 6px ${s.color}88)` : "none" }} />
          </g>
        ))}
        {series.filter((s) => s.key !== "origin").map((s) => {
          const idx = s.v.indexOf(Math.max(...s.v));
          const x = PL + (idx / (RPM.length - 1)) * (W - PL - PR);
          const y = PT + (1 - s.v[idx] / yMax) * (H - PT - PB);
          return <circle key={s.key} cx={x} cy={y} r="4" fill={s.color} style={{ opacity: drawn ? 1 : 0, transition: "opacity .5s ease 1.6s" }} />;
        })}
      </svg>
      {series.filter((s) => s.key !== "origin").map((s, i) => {
        const idx = s.v.indexOf(Math.max(...s.v));
        const left = ((PL + (idx / (RPM.length - 1)) * (W - PL - PR)) / W) * 100 + (i ? 5 : -5);
        const top = ((PT + (1 - s.v[idx] / yMax) * (H - PT - PB)) / H) * 100;
        return (
          <span key={s.key} className="absolute -translate-x-1/2 -translate-y-[150%] font-display text-xs sm:text-sm font-black whitespace-nowrap" style={{ left: `${left}%`, top: `${top}%`, color: s.color, opacity: drawn ? 1 : 0, transition: `opacity .5s ease ${1.6 + i * 0.35}s` }} data-testid={`dyno-peak-${s.key}`}>{s.hp} ch</span>
        );
      })}
      </div>
      <p className="text-[10px] text-neutral-500 mt-2">Simulation indicative à partir des valeurs maximales{nmGain ? ` · couple +${nmGain} Nm en Stage 1` : ""}. Courbes réelles mesurées au banc dans nos centres.</p>
    </div>
  );
}
